import { ChevronLeft as ArrowBackIosIcon, ChevronRight as ArrowForwardIosIcon, ShoppingCart } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import FormatPrice from "../../../../components/FormatPrice.jsx";
import { addProductToCart } from "../../../products/ProductToCart.jsx";
import { useDomain } from "../../../../context/DomainContext.jsx";
import { useCart } from "../../../../context/CartContext.jsx";
import { useNavigate } from "react-router";
import { useDispatch } from "react-redux";
import { setSelectedProduct } from "../../../../api/reducers/selectedProductReducer.jsx";
import RetailRichTextDescription from "../../RetailRichTextDescription";

const ScrollableFeaturedProducts = ({ productList, loggedIn }) => {
  const { addToCart, updateCart } = useCart();
  const domain = useDomain();
  const scrollContainerRef = useRef(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const viewProductDetail = (product) => {
    if (loggedIn) {
      dispatch(setSelectedProduct(product));
      navigate("/sf/product-info");
    } else {
      navigate("/sign-in");
    }
  };

  const handleProductClick = (product) => {
    if (loggedIn) {
      if (domain === "EDUCATION") {
        viewProductDetail(product);
      } else {
        addProductToCart({
          addToCart,
          navigate,
          productDetails: product,
          quantity: 1,
          updateCart,
        });
      }
    } else {
      navigate("/sign-in");
    }
  };

  const scrollByProductCount = (count) => {
    const productWidth = 320;
    return productWidth * count;
  };

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: -scrollByProductCount(2),
        behavior: "smooth",
      });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: scrollByProductCount(2),
        behavior: "smooth",
      });
    }
  };

  const checkScrollPosition = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setAtStart(scrollLeft === 0);
      setAtEnd(scrollLeft + clientWidth >= scrollWidth);
    }
  };

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", checkScrollPosition);
      checkScrollPosition();
    }
    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener("scroll", checkScrollPosition);
      }
    };
  }, []);

  return (
    <div className="relative mt-8">
      <h2 className="text-3xl sm:text-4xl font-bold mb-8 text-center text-gray-800">
        Featured Products
      </h2>
      <div className="flex items-center relative">
        {!atStart && (
          <button
            className="absolute left-0 bg-white bg-opacity-80 p-3 rounded-full shadow-lg z-10 hover:bg-opacity-100 transition duration-200"
            onClick={scrollLeft}
          >
            <ArrowBackIosIcon fontSize="large" />
          </button>
        )}
        <div
          ref={scrollContainerRef}
          className="flex overflow-x-auto scrollbar-hide space-x-6 px-6"
          style={{
            scrollSnapType: "x mandatory",
            scrollBehavior: "smooth",
          }}
        >
          {productList &&
            productList.map((productItem) => (
              <div
                key={productItem?._id}
                className="min-w-[280px] md:min-w-[320px] max-w-[320px] bg-white shadow-lg p-6 rounded-xl overflow-hidden transform hover:scale-105 transition-transform duration-300"
                style={{ minHeight: "480px", height: "auto", scrollSnapAlign: "start" }}
              >
                <img
                  loading="lazy"
                  src={productItem?.imageContents?.[0]?.displayLink || "/default-image.jpg"}
                  alt={productItem?.name || "Product Image"}
                  className="h-56 md:h-64 object-cover mb-4 w-full rounded-lg shadow-md transition-transform duration-300 transform hover:scale-105"
                />
                <h3
                  className="text-lg md:text-xl font-semibold cursor-pointer text-[#B88E2F] hover:text-[#A67C2A]"
                  onClick={() => viewProductDetail(productItem)}
                >
                  {productItem.name}
                </h3>
                <RetailRichTextDescription content={productItem.description} className="mt-2 md:mt-3" />
                <div className="mt-3 md:mt-4 text-xl md:text-2xl font-medium">
                  {productItem.price && (
                    <FormatPrice
                      code={productItem.price.currency || "USD"}
                      price={productItem.price.priceAmt}
                    />
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => handleProductClick(productItem)}
                  className="mt-4 bg-[#B88E2F] text-white rounded-full px-4 py-3 w-full flex items-center justify-center hover:bg-[#A67C2A] transition duration-200 text-sm md:text-base"
                >
                  <span>{domain === "EDUCATION" ? "View Schedule" : "Add to Cart"}</span>
                  <ShoppingCart className="ml-2" />
                </button>
              </div>
            ))}
        </div>
        {!atEnd && (
          <button
            className="absolute right-0 bg-white bg-opacity-80 p-3 rounded-full shadow-lg z-10 hover:bg-opacity-100 transition duration-200"
            onClick={scrollRight}
          >
            <ArrowForwardIosIcon fontSize="large" />
          </button>
        )}
      </div>
    </div>
  );
};

export default ScrollableFeaturedProducts;
